import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  HelpCircle,
  MessageSquare,
  Clock,
  Shield,
  Zap,
  Users,
  Phone,
  Mail,
} from "lucide-react";

const ContactFAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const faqData = [
    {
      icon: Clock,
      question: "What are your support hours?",
      answer:
        "Our support team is available 24/7 for critical issues. For general inquiries, our business hours are Monday-Friday 8:00 AM - 5:00 PM GMT. We typically respond to emails within 1 hour during business hours.",
      category: "Support",
    },
    {
      icon: MessageSquare,
      question: "How quickly will I receive a response?",
      answer:
        "Response times vary by contact method: Phone calls are answered within 2 rings during business hours, emails are typically answered within 1 hour during business hours, and within 4 hours outside business hours. For technical support emails, we usually respond within 30 minutes during business hours.",
      category: "Response Time",
    },
    {
      icon: Phone,
      question: "Do you offer phone support?",
      answer:
        "Yes! We offer comprehensive phone support with multiple numbers for different needs. Our main support line handles general inquiries, while our technical support line is dedicated to integration and technical issues. Emergency support is available 24/7 for critical issues.",
      category: "Support",
    },
    {
      icon: Shield,
      question: "Is my information secure when I contact you?",
      answer:
        "Yes, we take data security seriously. All communications are encrypted, and we follow strict privacy policies. We never share your contact information with third parties and only use it to respond to your inquiries and improve our services.",
      category: "Security",
    },
    {
      icon: Zap,
      question: "What if I have an urgent technical issue?",
      answer:
        "For urgent technical issues, call our emergency support line immediately. We prioritize critical issues that affect your messaging campaigns and aim to resolve them within 15 minutes. You can also mark your email as 'URGENT' in the subject line for faster processing.",
      category: "Emergency",
    },
    {
      icon: HelpCircle,
      question: "What information should I include when contacting support?",
      answer:
        "To help us assist you faster, please include: your account email, a clear description of the issue, steps you've already tried, any error messages you're seeing, and the urgency level. Screenshots or screen recordings are also very helpful for technical issues.",
      category: "Best Practices",
    },
    {
      icon: Mail,
      question: "Do you offer support in multiple languages?",
      answer:
        "Yes, we offer support in English, French, and Hausa. Our team can also assist with basic inquiries in other West African languages. Simply let us know your preferred language when you contact us via email or phone.",
      category: "Languages",
    },
    {
      icon: Users,
      question: "Can I schedule a consultation call?",
      answer:
        "Yes! For complex inquiries or if you need detailed guidance on using our platform, you can request a scheduled consultation call. Simply mention this in your email or ask when you call our support line, and we'll arrange a convenient time for a dedicated consultation session.",
      category: "Consultation",
    },
  ];

  const categories = [...new Set(faqData.map((item) => item.category))];

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-red-50">
      <div className="px-4 mx-auto max-w-4xl sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="mb-12 text-center">
            <div className="inline-flex items-center px-4 py-2 mb-4 bg-gradient-to-r from-red-100 to-blue-100 rounded-full border border-red-200/50">
              <HelpCircle className="mr-2 w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-700">
                Frequently Asked Questions
              </span>
            </div>
            <h2 className="mb-4 text-3xl font-bold text-gray-800 md:text-4xl">
              Quick Answers to Common Questions
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Find answers to the most common questions about contacting our
              support team. Can't find what you're looking for? Don't hesitate
              to reach out!
            </p>
          </motion.div>

          {/* Category Pills */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap gap-2 justify-center mb-8"
          >
            {categories.map((category, index) => (
              <span
                key={index}
                className="px-3 py-1 text-sm font-medium text-gray-600 bg-white rounded-full border border-gray-200 transition-colors duration-300 hover:bg-gray-50"
              >
                {category}
              </span>
            ))}
          </motion.div>

          {/* FAQ Items */}
          <motion.div variants={itemVariants} className="space-y-4">
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                initial={false}
                className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-lg transition-shadow duration-300 hover:shadow-xl"
              >
                <motion.button
                  onClick={() => toggleFAQ(index)}
                  className="flex justify-between items-center p-6 w-full text-left rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  whileHover={{ backgroundColor: "rgb(249, 250, 251)" }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-br from-red-100 to-blue-100 rounded-xl">
                      <faq.icon className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-left text-gray-800">
                        {faq.question}
                      </h3>
                      <span className="inline-block px-2 py-1 mt-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
                        {faq.category}
                      </span>
                    </div>
                  </div>
                  <motion.div
                    animate={{ rotate: activeIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0 ml-4"
                  >
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  </motion.div>
                </motion.button>

                <AnimatePresence>
                  {activeIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <div className="pl-14">
                          <p className="leading-relaxed text-gray-600">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>

          {/* Still Have Questions CTA */}
          <motion.div variants={itemVariants} className="mt-12 text-center">
            <div className="p-8 text-white bg-gradient-to-r from-red-600 to-blue-600 rounded-2xl">
              <h3 className="mb-4 text-2xl font-bold">Still Have Questions?</h3>
              <p className="mx-auto mb-6 max-w-2xl text-red-100">
                Can't find the answer you're looking for? Our support team is
                here to help! Reach out through phone or email and we'll get
                back to you quickly.
              </p>
              <div className="flex flex-col gap-4 justify-center sm:flex-row">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() =>
                    document
                      .getElementById("contact-form")
                      .scrollIntoView({ behavior: "smooth" })
                  }
                  className="px-6 py-3 font-semibold text-red-600 bg-white rounded-lg transition-colors duration-300 hover:bg-gray-50"
                >
                  <MessageSquare className="inline mr-2 w-4 h-4" />
                  Send us a Message
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (window.location.href = "tel:+2348031234567")}
                  className="px-6 py-3 font-semibold text-white rounded-lg border backdrop-blur-sm transition-colors duration-300 bg-white/20 border-white/30 hover:bg-white/30"
                >
                  <Phone className="inline mr-2 w-4 h-4" />
                  Call Support
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactFAQ;
